import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Shield, Users, Calendar, FileText, CheckCircle, Settings, ClipboardList, UserCheck, BarChart3, X } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "../contexts/TenantContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dynamic Logo Component
const TenantLogo = ({ className = "h-10", inverted = false, onDarkBg = false, logoUrl = "/logo.png" }) => {
  // When inverted (for footer/dark sections with solid bg)
  if (inverted) {
    return (
      <img 
        src={logoUrl}
        alt="Logo"
        className={`${className} brightness-0 invert`}
        style={{ objectFit: 'contain', background: 'transparent' }}
      />
    );
  }
  
  // On dark background - apply rounded corners and slight shadow to look better
  if (onDarkBg) {
    return (
      <div className={`${className} flex items-center justify-center bg-white rounded-lg shadow-lg overflow-hidden p-1`}>
        <img 
          src={logoUrl}
          alt="Logo"
          className="h-full w-auto"
          style={{ objectFit: 'contain' }}
        />
      </div>
    );
  }
  
  // On light background - use multiply to handle white bg
  return (
    <img 
      src={logoUrl}
      alt="Logo"
      className={className}
      style={{ 
        objectFit: 'contain',
        background: 'transparent',
        mixBlendMode: 'multiply',
      }}
    />
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checking, setChecking] = useState(true);
  const [useStandaloneAuth, setUseStandaloneAuth] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeGuide, setActiveGuide] = useState("planlama");
  
  // DevTools blocker for landing page (security)
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    // Disable keyboard shortcuts for devtools
    const handleKeyDown = (e) => {
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (view source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
    };
    
    // DevTools open detection via window size
    const checkDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      
      if (widthThreshold || heightThreshold) {
        document.body.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            background: #1f2937;
            color: white;
            font-family: system-ui, -apple-system, sans-serif;
            text-align: center;
            padding: 20px;
          ">
            <div style="font-size: 48px; margin-bottom: 20px;">🔒</div>
            <h1 style="font-size: 24px; margin-bottom: 10px;">Güvenlik Uyarısı</h1>
            <p style="color: #9ca3af; margin-bottom: 20px;">Geliştirici araçları bu sayfada kullanılamaz.</p>
            <button onclick="window.location.reload()" style="
              background: #3b82f6;
              color: white;
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-size: 16px;
            ">Sayfayı Yenile</button>
          </div>
        `;
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    const devToolsInterval = setInterval(checkDevTools, 1000);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      clearInterval(devToolsInterval);
    };
  }, []);
  
  // Get tenant info
  const { tenant, getTenantLogo, getTenantName, getPrimaryColor, loading: tenantLoading } = useTenant();
  
  // Tenant-specific content
  const tenantContent = {
    trb: {
      companyName: "TRB ULUSLARARASI",
      subtitle: "BELGELENDİRME",
      tagline: "GSTC Akredite · Sürdürülebilir Turizm Denetimi",
      description: "TRB Uluslararası Belgelendirme tarafından geliştirilen, GSTC ve TR-I kriterlerine uygun sürdürülebilir turizm denetim platformu.",
      primaryColor: "#16a34a",
      accentColor: "#B91C1C"
    },
    royalcert: {
      companyName: "ROYALCERT",
      subtitle: "BELGELENDİRME",
      tagline: "GSTC Akredite · Sürdürülebilir Turizm Denetimi",
      description: "RoyalCert Belgelendirme tarafından geliştirilen, GSTC kriterlerine uygun sürdürülebilir turizm denetim platformu.",
      primaryColor: "#b91c1c",
      accentColor: "#1f2937"
    }
  };
  
  // Get current tenant content or default to TRB
  const currentTenant = tenant?.slug || 'trb';
  const content = tenantContent[currentTenant] || tenantContent.trb;
  const logoUrl = getTenantLogo();
  const primaryColor = getPrimaryColor();
  const companyName = getTenantName();

  useEffect(() => {
    checkSetupStatus();
    checkAuthMode();
    
    const error = searchParams.get('error');
    if (error) {
      toast.error(decodeURIComponent(error));
    }
  }, [searchParams]);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API}/setup/status`);
      setNeedsSetup(response.data.needs_setup);
    } catch (error) {
      console.error("Setup check error:", error);
    } finally {
      setChecking(false);
    }
  };

  const checkAuthMode = async () => {
    const isProductionDomain = !window.location.hostname.includes('emergentagent.com') && 
                               !window.location.hostname.includes('localhost');
    
    if (isProductionDomain) {
      setUseStandaloneAuth(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/google/url`);
      if (response.data.auth_url) {
        setUseStandaloneAuth(true);
      }
    } catch (error) {
      setUseStandaloneAuth(false);
    }
  };

  const handleLogin = async () => {
    if (useStandaloneAuth) {
      try {
        const response = await axios.get(`${API}/auth/google/url`);
        window.location.href = response.data.auth_url;
      } catch (error) {
        toast.error("Giriş hatası. Lütfen tekrar deneyin.");
      }
    } else {
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const features = [
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "1. Müşteri & Planlama",
      description: "Müşteri kaydı oluşturun, adam-gün hesabı yapın ve denetim planlayın"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "2. Denetim & Raporlama",
      description: "GSTC/TR-I checklist'i doldurun, kanıt ve fotoğraf ekleyin"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "3. Onay & Belgelendirme",
      description: "Teknik inceleme, FR.48 karar formu ve sertifika oluşturma"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "4. Takip & Gözetim",
      description: "Sertifika yönetimi, yıllık gözetim ve yeniden belgelendirme"
    }
  ];

  // Usage guides content
  const guides = {
    planlama: {
      title: "Planlama Uzmanı Kılavuzu",
      icon: <ClipboardList className="w-6 h-6" />,
      sections: [
        {
          title: "1. Müşteri Ekleme",
          steps: [
            "Sol menüden 'Müşteriler' sekmesine gidin",
            "'Yeni Müşteri' butonuna tıklayın",
            "Müşteri bilgilerini doldurun (Firma adı, e-posta, telefon vb.)",
            "Tesis bilgilerini girin (Tesis adı, ID, oda/yatak sayısı vb.)",
            "'Kaydet' butonuna tıklayın"
          ]
        },
        {
          title: "2. Denetim Planlama",
          steps: [
            "Sol menüden 'Denetimler' sekmesine gidin",
            "'Yeni Denetim' butonuna tıklayın",
            "Müşteri seçin ve denetim tarihini belirleyin",
            "Denetçi atayın",
            "'Kaydet' ile denetimi oluşturun"
          ]
        },
        {
          title: "3. Excel İçe/Dışa Aktarma",
          steps: [
            "Müşteriler sayfasında 'Excel'den İçe Aktar' ile toplu müşteri ekleyin",
            "'Excel'e Aktar' ile mevcut müşterileri indirin",
            "Şablon dosyasını kullanarak veri hazırlayın"
          ]
        }
      ]
    },
    denetci: {
      title: "Denetçi Kılavuzu",
      icon: <UserCheck className="w-6 h-6" />,
      sections: [
        {
          title: "1. Atanan Denetimleri Görüntüleme",
          steps: [
            "Dashboard'da size atanan denetimleri görün",
            "'Denetimler' sekmesinden detaylara erişin",
            "Denetim tarihini ve müşteri bilgilerini kontrol edin"
          ]
        },
        {
          title: "2. Rapor Oluşturma",
          steps: [
            "Denetim detayında 'Rapor Oluştur' butonuna tıklayın",
            "GSTC kriterlerini değerlendirin (Uygun/Minör/Majör/Gözlem)",
            "Her kriter için kanıt ve açıklama yazın",
            "Fotoğraf ekleyin",
            "'Kaydet' ile raporu kaydedin"
          ]
        },
        {
          title: "3. Raporu Onaya Gönderme",
          steps: [
            "Görüşülen kişileri ekleyin (Mülakat sekmesi)",
            "Toplantı katılımcılarını girin",
            "Tüm kriterleri değerlendirin",
            "'Onaya Gönder' butonuna tıklayın"
          ]
        },
        {
          title: "4. Belge İndirme",
          steps: [
            "Rapor sayfasından Word export yapın",
            "Toplantı Tutanağı, Denetim Planı, Ekip Atama formlarını indirin",
            "Uygunsuzluk raporlarını oluşturun"
          ]
        }
      ]
    },
    teknik: {
      title: "Teknik Yönetici Kılavuzu",
      icon: <BarChart3 className="w-6 h-6" />,
      sections: [
        {
          title: "1. Rapor İnceleme",
          steps: [
            "Sol menüden 'Rapor Onay' sekmesine gidin",
            "Bekleyen raporları listeleyin",
            "Rapor detayına tıklayarak inceleyin"
          ]
        },
        {
          title: "2. Rapor Onaylama/Reddetme",
          steps: [
            "Rapordaki tüm kriterleri gözden geçirin",
            "Fotoğraf ve kanıtları kontrol edin",
            "'Onayla' veya 'Reddet' butonlarını kullanın",
            "Red durumunda gerekçe yazın"
          ]
        },
        {
          title: "3. Google Drive Entegrasyonu",
          steps: [
            "Rapor onaylandığında belgeler otomatik Drive'a yüklenir",
            "'Ayarlar' sayfasından Drive bağlantısını kontrol edin",
            "Klasör: '[Müşteri Adı] - [Tarih]' formatında oluşturulur"
          ]
        },
        {
          title: "4. Dashboard İzleme",
          steps: [
            "Aylık denetim istatistiklerini takip edin",
            "Onay bekleyen rapor sayısını görün",
            "Müşteri ve denetim durumlarını izleyin"
          ]
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Setup Banner */}
      {!checking && needsSetup && (
        <div className="bg-[#B91C1C] text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              <span>Sistem kurulumu gerekli. Lütfen ilk admin hesabını oluşturun.</span>
            </div>
            <Button 
              onClick={() => navigate("/setup")}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#B91C1C]"
              data-testid="setup-banner-btn"
            >
              Kuruluma Git
            </Button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative min-h-screen">
        {/* Background with gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f2937] via-[#374151] to-[#4b5563]">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <header className="px-6 lg:px-12 py-6">
            <nav className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-3">
                <TenantLogo className="h-16 w-auto" onDarkBg={true} logoUrl={logoUrl} />
                <div className="hidden sm:block">
                  <h1 className="text-white font-bold text-lg leading-tight">{content.companyName}</h1>
                  <p className="text-white/80 text-sm">{content.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {needsSetup && (
                  <Button 
                    onClick={() => navigate("/setup")}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                    data-testid="header-setup-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Kurulum
                  </Button>
                )}
                <Button 
                  onClick={() => setShowLoginModal(true)}
                  data-testid="header-login-btn"
                  className="text-white hover:opacity-90 font-medium px-6"
                  style={{ backgroundColor: primaryColor }}
                  disabled={needsSetup}
                >
                  Giriş Yap
                </Button>
              </div>
            </nav>
          </header>

          {/* Hero Content */}
          <div className="px-6 lg:px-12 pt-20 lg:pt-32 pb-20 max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm mb-8">
                <Shield className="w-4 h-4" style={{ color: primaryColor }} />
                <span>{content.tagline}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
                Profesyonel Denetim
                <span className="block" style={{ color: primaryColor }}>Yönetim Sistemi</span>
              </h1>
              
              <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-xl">
                {content.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {needsSetup ? (
                  <Button 
                    onClick={() => navigate("/setup")}
                    size="lg"
                    className="text-white font-medium px-8 h-12 hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="hero-setup-btn"
                  >
                    <Settings className="w-5 h-5 mr-2" />
                    Kurulumu Başlat
                  </Button>
                ) : (
                  <Button 
                    onClick={() => setShowLoginModal(true)}
                    data-testid="hero-login-btn"
                    size="lg"
                    className="text-white font-medium px-8 h-12 hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Google ile Başla
                  </Button>
                )}
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-white/30 text-white hover:bg-white/10 font-medium px-8 h-12"
                  onClick={() => setShowGuideModal(true)}
                  data-testid="info-btn"
                >
                  Kullanma Kılavuzu
                </Button>
              </div>

              {/* Stats */}
              <div className="flex gap-12 mt-16 pt-8 border-t border-white/10">
                <div>
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-white/60 text-sm mt-1">Denetim</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">150+</div>
                  <div className="text-white/60 text-sm mt-1">Müşteri</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">%98</div>
                  <div className="text-white/60 text-sm mt-1">Memnuniyet</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-4">
              Denetim Süreci
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Uçtan uca dijital denetim yönetimi - başvurudan sertifikaya
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl bg-gray-50 border border-gray-200 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#B91C1C]/10 flex items-center justify-center text-[#B91C1C] mb-4 group-hover:bg-[#B91C1C] group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section 2 */}
      <section className="py-20 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-6">
                Entegre Yönetim Araçları
              </h2>
              <div className="space-y-4">
                {[
                  { role: "Mesajlaşma Sistemi", desc: "Ekip içi anlık iletişim ve dosya paylaşımı", color: "text-[#B91C1C]" },
                  { role: "Bildirim Merkezi", desc: "Görev hatırlatmaları ve süreç güncellemeleri", color: "text-[#B91C1C]" },
                  { role: "Masraf Yönetimi", desc: "Denetim giderlerini takip edin ve raporlayın", color: "text-[#B91C1C]" },
                  { role: "Takvim & Planlama", desc: "Ekip takvimi ve denetim programı görünümü", color: "text-[#B91C1C]" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200 hover:border-[#B91C1C]/30 transition-colors">
                    <CheckCircle className={`w-5 h-5 ${item.color} mt-0.5 flex-shrink-0`} />
                    <div>
                      <div className="font-medium text-gray-900">{item.role}</div>
                      <div className="text-sm text-gray-600">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl p-8 text-white" style={{ background: `linear-gradient(to bottom right, ${primaryColor}, ${primaryColor}dd)` }}>
                <TenantLogo className="h-16 mb-6 brightness-0 invert" logoUrl={logoUrl} />
                <h3 className="text-2xl font-bold mb-4">GSTC & TR-I Belgelendirme</h3>
                <p className="text-white/80 mb-6">
                  Uluslararası standartlarda sürdürülebilir turizm denetimi ve belgelendirme süreçleri
                </p>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">42</div>
                    <div className="text-sm text-white/70">GSTC Kriteri</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">15+</div>
                    <div className="text-sm text-white/70">FR Formu</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <TenantLogo className="h-10 brightness-0 invert" logoUrl={logoUrl} />
            <span className="text-gray-400">© 2026 {companyName}</span>
          </div>
          <div className="flex gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Gizlilik Politikası
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Kullanım Koşulları
            </Link>
          </div>
        </div>
      </footer>

      {/* Usage Guide Modal */}
      <Dialog open={showGuideModal} onOpenChange={setShowGuideModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-6 h-6" style={{ color: primaryColor }} />
              Kullanım Kılavuzu
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col md:flex-row gap-4 flex-1 overflow-hidden mt-4">
            {/* Tab buttons */}
            <div className="flex md:flex-col gap-2 md:w-48 flex-shrink-0">
              {Object.entries(guides).map(([key, guide]) => (
                <button
                  key={key}
                  onClick={() => setActiveGuide(key)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-left transition-all ${
                    activeGuide === key 
                      ? "text-white" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  style={activeGuide === key ? { backgroundColor: primaryColor } : {}}
                >
                  {guide.icon}
                  <span className="font-medium text-sm whitespace-nowrap">
                    {key === "planlama" ? "Planlama" : key === "denetci" ? "Denetçi" : "Teknik Yön."}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto pr-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                {guides[activeGuide].icon}
                {guides[activeGuide].title}
              </h3>
              
              <div className="space-y-6">
                {guides[activeGuide].sections.map((section, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium mb-3" style={{ color: primaryColor }}>{section.title}</h4>
                    <ul className="space-y-2">
                      {section.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-5 h-5 rounded-full bg-[#B91C1C]/10 text-[#B91C1C] flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {stepIdx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-4 pt-4 border-t">
            <Button onClick={() => setShowGuideModal(false)} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center text-center py-4">
            {/* Logo inside the modal */}
            <div className="mb-6">
              <TenantLogo className="h-24 w-auto" onDarkBg={false} logoUrl={logoUrl} />
            </div>
            
            <DialogHeader className="w-full">
              <DialogTitle className="text-2xl font-bold text-gray-900 text-center">
                Hoş Geldiniz
              </DialogTitle>
            </DialogHeader>
            
            <p className="text-gray-600 mt-2 mb-6">
              Denetim Yönetim Sistemine giriş yapmak için Google hesabınızı kullanın
            </p>
            
            <Button 
              onClick={handleLogin}
              data-testid="modal-google-login-btn"
              className="w-full text-white font-medium h-12 text-base hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google ile Giriş Yap
            </Button>
            
            <div className="mt-6 pt-4 border-t border-gray-200 w-full">
              <p className="text-xs text-gray-500">
                Giriş yaparak{" "}
                <Link to="/terms" className="hover:underline" style={{ color: primaryColor }}>
                  kullanım şartlarını
                </Link>{" "}
                kabul etmiş olursunuz.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
